import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationType } from '../enums/NotificationType';
import { OnlineOrderType } from '../models/OnlineOrder.model';
import { AppSettingsService } from './app-settings.service';
import { CartService } from './cart.service';
import { PBService } from './pocketbase.service';

export const WAITER_COOLDOWN_STORAGE_KEY = 'bistronex_qr_waiter_cooldown_until';

export interface WaiterToast {
  text: string;
  type: 'success' | 'warning' | 'error';
  id: number;
}

@Injectable({ providedIn: 'root' })
export class WaiterCallService {
  private pb = inject(PBService);
  private settings = inject(AppSettingsService);
  private cartService = inject(CartService);
  private translate = inject(TranslateService);
  private ngZone = inject(NgZone);

  public NotificationType = NotificationType;

  public isSubmitting = signal<boolean>(false);
  public submittingType = signal<NotificationType | null>(null);
  public cooldownSeconds = signal<number>(0);
  public toast = signal<WaiterToast | null>(null);

  private timerInterval: any = null;
  private toastTimeout: any = null;

  public isCooldownActive = computed(() => this.cooldownSeconds() > 0);
  public isBusy = computed(() => this.isSubmitting() || this.isCooldownActive());

  public effectiveTableID = computed(() => {
    const directTable = this.settings.tableID();
    if (directTable && directTable.trim().length > 0) {
      return directTable;
    }
    const activeTableOrder = this.cartService
      .activeOrders()
      .find((o) => o.TableID && o.TableID.trim().length > 0);
    if (activeTableOrder?.TableID) {
      return activeTableOrder.TableID;
    }
    return null;
  });

  public isTableCallEnabled = computed(() => this.settings.tableCallEnabled());

  public isTableSeated = computed(() => {
    const tableId = this.effectiveTableID();
    if (!tableId) return false;
    // For takeaway/pickup, hide or disable them
    if (this.cartService.orderType() === OnlineOrderType.PICKUP && !this.settings.tableID()) {
      return false;
    }
    return true;
  });

  public isTableCallAvailable = computed(() => {
    return this.isTableCallEnabled() && this.isTableSeated();
  });

  public tableDisplayName = computed(() => {
    const curTable = this.settings.currentTable();
    if (curTable?.Name) return curTable.Name;
    const tid = this.effectiveTableID();
    return tid ? `#${tid}` : '';
  });

  constructor() {
    this.restoreCooldown();
  }

  private restoreCooldown() {
    try {
      const stored = localStorage.getItem(WAITER_COOLDOWN_STORAGE_KEY);
      if (stored) {
        const until = parseInt(stored, 10);
        const now = Date.now();
        if (until > now) {
          const remaining = Math.ceil((until - now) / 1000);
          this.startCooldown(remaining, until);
        } else {
          this.clearStoredCooldown();
        }
      }
    } catch (_) { }
  }

  public startCooldown(seconds: number, existingUntil?: number) {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    const until = existingUntil || Date.now() + seconds * 1000;
    try {
      localStorage.setItem(WAITER_COOLDOWN_STORAGE_KEY, until.toString());
    } catch (_) { }

    this.cooldownSeconds.set(seconds);

    this.ngZone.runOutsideAngular(() => {
      this.timerInterval = setInterval(() => {
        const remaining = Math.ceil((until - Date.now()) / 1000);
        this.ngZone.run(() => {
          if (remaining > 0) {
            this.cooldownSeconds.set(remaining);
          } else {
            this.cooldownSeconds.set(0);
            this.clearStoredCooldown();
            if (this.timerInterval) {
              clearInterval(this.timerInterval);
              this.timerInterval = null;
            }
          }
        });
      }, 1000);
    });
  }

  private clearStoredCooldown() {
    try {
      localStorage.removeItem(WAITER_COOLDOWN_STORAGE_KEY);
    } catch (_) { }
  }

  public showToast(text: string, type: 'success' | 'warning' | 'error') {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }

    this.toast.set({ text, type, id: Date.now() });

    this.toastTimeout = setTimeout(() => {
      this.toast.set(null);
      this.toastTimeout = null;
    }, 4500);
  }

  public dismissToast() {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }
    this.toast.set(null);
  }

  async callWaiter(description?: string): Promise<boolean> {
    return this.sendNotification(NotificationType.TABLE_CALL, description);
  }

  async requestBill(description?: string): Promise<boolean> {
    return this.sendNotification(NotificationType.BILL_REQUEST, description);
  }

  async sendNotification(type: NotificationType, description?: string): Promise<boolean> {
    if (!this.settings.tableCallEnabled() || this.isBusy()) {
      return false;
    }

    const customerId = (this.settings.venue || this.settings.customer)?.id;
    const tableId = this.effectiveTableID();

    if (!customerId) {
      const err =
        this.translate.instant('WAITER.ERROR_VENUE') || this.translate.instant('WAITER.ERROR_CUSTOMER') ||
        'Customer information not loaded.';
      this.showToast(err, 'error');
      return false;
    }

    if (!tableId) {
      const err =
        this.translate.instant('WAITER.ERROR_NO_TABLE') ||
        'Table information is missing.';
      this.showToast(err, 'error');
      return false;
    }

    this.isSubmitting.set(true);
    this.submittingType.set(type);

    try {
      await this.pb.sendNotification({
        VenueID: customerId,
        CustomerID: customerId,
        Type: type,
        TableID: tableId,
        Description: description?.trim() || undefined,
      });

      this.isSubmitting.set(false);
      this.submittingType.set(null);

      // On success: start 12-second cooldown
      this.startCooldown(12);

      const msgKey =
        type === NotificationType.TABLE_CALL
          ? 'WAITER.SUCCESS_CALL'
          : 'WAITER.SUCCESS_BILL';
      const defaultMsg =
        type === NotificationType.TABLE_CALL
          ? 'Waiter has been called! A staff member is on the way.'
          : 'Bill requested. A staff member is on the way.';
      const successText = this.translate.instant(msgKey) || defaultMsg;
      this.showToast(successText, 'success');
      return true;
    } catch (err: any) {
      this.isSubmitting.set(false);
      this.submittingType.set(null);

      if (err?.status === 429) {
        let remaining = err?.remainingSeconds;
        if (!remaining && typeof err?.message === 'string') {
          const match = err.message.match(/(\d+)\s*sec/i);
          if (match) remaining = parseInt(match[1], 10);
        }
        if (!remaining || remaining <= 0) remaining = 12;

        this.startCooldown(remaining);

        const rateMsg =
          this.translate.instant('WAITER.RATE_LIMITED', { seconds: remaining }) ||
          err?.message ||
          `Please wait ${remaining} seconds before calling again.`;
        this.showToast(rateMsg, 'warning');
      } else {
        const errorMsg =
          err?.message ||
          this.translate.instant('WAITER.ERROR_GENERIC') ||
          'Failed to send request. Please try again.';
        this.showToast(errorMsg, 'error');
      }
      return false;
    }
  }
}
